import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';

type ToastType = 'success' | 'info' | 'warning' | 'error';

interface ToastOptions {
  type?: ToastType;
  durationMs?: number;
}

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  durationMs: number;
}

interface ToastContextType {
  showToast: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const slideIn = keyframes`
  from { transform: translateY(12px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  z-index: 1000;

  @media (max-width: 640px) {
    top: 16px;
  }
`;

const ToastCard = styled.button<{ type: ToastType }>`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 320px;
  max-width: min(92vw, 560px);
  padding: 14px 18px;
  border-radius: var(--border-radius-2xl, 20px);
  box-shadow: var(--shadow-lg);
  background: linear-gradient(180deg, #ffffff 0%, #fafafa 100%);
  border: 1px solid rgba(2,6,23,0.08);
  color: var(--color-text);
  font-weight: 600;
  font-size: var(--font-size-base);
  animation: ${slideIn} 160ms ease both;
  cursor: pointer;
  text-align: left;

  &:hover { box-shadow: 0 12px 28px rgba(2,6,23,0.15); }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 9999px;
    background-color:
      ${({ type }) =>
        type === 'success' ? 'var(--color-success)' :
        type === 'warning' ? 'var(--color-warning)' :
        type === 'error' ? 'var(--color-error)' : 'var(--color-secondary)'};
    flex: 0 0 auto;
  }

  .message { flex: 1 1 auto; }
`;

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback((message: string, options?: ToastOptions) => {
    const id = Date.now() + Math.random();
    const item: ToastItem = {
      id,
      message,
      type: options?.type || 'success',
      durationMs: options?.durationMs ?? 3000,
    };
    setToasts(prev => [...prev, item]);
  }, []);

  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map(t => setTimeout(() => {
      setToasts(prev => prev.filter(x => x.id !== t.id));
    }, t.durationMs));
    return () => timers.forEach(clearTimeout);
  }, [toasts]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer role="status" aria-live="polite">
        {toasts.map(t => (
          <ToastCard
            key={t.id}
            type={t.type}
            onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
            title="Klicka för att stänga"
          >
            <span className="dot" />
            <span className="message">{t.message}</span>
          </ToastCard>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};
